import { useEffect, useMemo, useState } from 'react';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import useAuth from '../../../hooks/useAuth';
import { isMaster } from '../../../utils/perfil';
import { Num } from '../../../components/gps';
import {
  novidadesItems,
  NOVIDADES_RELEASE_DATE,
  NOVIDADES_STATUS,
  NOVIDADES_STORAGE_KEY,
  NOVIDADES_VERSION,
} from '../../../sections/dashboard/novidades/novidadesData';

/* ============================================================================
   Novidades - visual novo (shadcn + Tailwind).

   Reescrita no lugar: e tela de LEITURA, sem nenhuma chamada de API. O conteudo
   vem de sections/dashboard/novidades/novidadesData.ts, que nao foi tocado.

   O efeito que marca as novidades como lidas (localStorage) foi preservado - e
   ele que apaga o selo do menu/rodape. Sem ele o aviso de "novidades" nunca
   desaparece.

   DUAS MELHORAS sobre a versao MUI:

   1. FILTRO POR VERSAO. A lista tem 30+ entradas acumuladas de varias versoes; a
      pergunta de quem abre e "o que mudou na versao que eu recebi agora". Sem
      filtro, isso e rolar procurando.

   2. OS DETALHES DE CADA ITEM ABREM E FECHAM. Na versao antiga todos os `details`
      apareciam sempre, o que fazia a pagina ter varios metros de altura e enterrava
      os itens novos no fim da rolagem.
   ============================================================================ */

NovidadesPage.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function NovidadesPage() {
  const { user } = useAuth();
  const [versao, setVersao] = useState('todas');
  const [aberto, setAberto] = useState<string | null>(null);

  // Marca como lido - e o que apaga o selo de novidades no menu.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(NOVIDADES_STORAGE_KEY, 'true');
  }, []);

  const versoes = useMemo(() => {
    const lista: string[] = [];
    novidadesItems.forEach((i) => {
      if (i.version && !lista.includes(i.version)) lista.push(i.version);
    });
    return lista;
  }, []);

  const itens = useMemo(
    () => {
      /* Mesma regra do Manual: entrada marcada `soMaster` some para quem nao e.
         O changelog e o outro lugar onde o nivel de acesso vazava - ele anuncia
         recurso novo sem perguntar quem pode usar. */
      const permitidas = novidadesItems.filter((i) => isMaster(user) || !i.soMaster);
      return versao === 'todas' ? permitidas : permitidas.filter((i) => i.version === versao);
    },
    // `user` tambem: se chegar depois do 1o render, o Master via a lista sem os itens dele.
    [versao, user]
  );

  return (
    <Page title="Novidades">
      <div className="mx-auto w-full max-w-[1000px] p-6 font-sans text-ink">
        {/* Cabecalho da versao atual. Substitui o bloco escuro com gradiente da
            versao antiga - o mesmo conteudo, sem o painel de 300px de altura. */}
        <div className="mb-5 rounded-lg border border-line bg-surface p-5 shadow-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-faint">
                Versão atual
              </span>
              <h1 className="mt-1 text-2xl font-extrabold tracking-[-0.03em]">
                GPShub <Num>{NOVIDADES_VERSION}</Num>
              </h1>
              <p className="mt-1 text-[12.5px] text-muted-foreground">
                Publicada em <Num>{NOVIDADES_RELEASE_DATE}</Num> · {NOVIDADES_STATUS}
              </p>
            </div>

            <span className="rounded-md bg-ok-soft px-2.5 py-1 text-[12px] font-bold text-ok">
              {NOVIDADES_STATUS}
            </span>
          </div>

          <p className="mt-3 max-w-[75ch] text-[12.5px] text-muted-foreground">
            O que mudou no sistema, da mudança mais recente para a mais antiga. Cada item pode ser aberto
            para ver o detalhe do que foi alterado.
          </p>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-faint">Versão</span>
          <button
            type="button"
            onClick={() => setVersao('todas')}
            className={`h-8 rounded-lg border px-2.5 text-[12px] font-bold ${
              versao === 'todas' ? 'border-primary bg-primary/10 text-ink' : 'border-line text-muted-foreground'
            }`}
          >
            Todas
          </button>
          {versoes.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVersao(v)}
              className={`h-8 rounded-lg border px-2.5 text-[12px] font-bold tabular-nums ${
                versao === v ? 'border-primary bg-primary/10 text-ink' : 'border-line text-muted-foreground'
              }`}
            >
              {v}
            </button>
          ))}

          <span className="ml-auto text-[12px] text-muted-foreground">
            <Num className="font-bold text-ink">{itens.length}</Num> item(ns)
          </span>
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
          {itens.map((item, i) => {
            const expandido = aberto === item.id;
            const temDetalhe = Boolean(item.details?.length);
            const maisRecente = versao === 'todas' && i === 0;

            return (
              <div key={item.id} className="border-b border-line last:border-b-0">
                <button
                  type="button"
                  onClick={() => temDetalhe && setAberto(expandido ? null : item.id)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left ${
                    temDetalhe ? 'hover:bg-surface-2' : 'cursor-default'
                  }`}
                >
                  <span
                    className={`mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-md text-[11px] font-bold ${
                      maisRecente ? 'bg-ok-soft text-ok' : 'bg-soft text-muted-foreground'
                    }`}
                  >
                    {temDetalhe ? (expandido ? '−' : '+') : '•'}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] font-bold">{item.title}</span>
                    <span className="mt-0.5 block text-[12px] text-muted-foreground">{item.description}</span>

                    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <Num className="rounded bg-soft px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        {item.date}
                      </Num>
                      <Num className="rounded bg-soft px-1.5 py-0.5 text-[11px] text-muted-foreground">
                        v{item.version}
                      </Num>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
                          maisRecente ? 'bg-ok-soft text-ok' : 'bg-warn-soft text-warn'
                        }`}
                      >
                        {item.status}
                      </span>
                    </span>
                  </span>
                </button>

                {expandido && item.details && (
                  <ul className="space-y-1 border-t border-line bg-surface-2 px-4 py-3 pl-[52px]">
                    {item.details.map((detalhe) => (
                      <li key={detalhe} className="flex gap-2 text-[12px] text-muted-foreground">
                        <span className="flex-none text-faint">–</span>
                        <span>{detalhe}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {!itens.length && (
            <div className="p-8 text-center text-[12.5px] text-muted-foreground">
              Nenhuma novidade nesta versão.
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
